import { SITE_NAME } from "@/config/site";

const PLAN_CODES = ["free", "per_project", "unlimited"] as const;
type PlanCode = (typeof PLAN_CODES)[number];

export function getProductPlanCode(metadata: Record<string, string> | undefined) {
  return metadata?.planCode;
}

export function resolvePlanCode(
  priceMetadata: Record<string, string> | undefined,
  productMetadata: Record<string, string> | undefined,
) {
  return priceMetadata?.planCode ?? productMetadata?.planCode;
}

export function resolveBillingInterval(
  priceMetadata: Record<string, string> | undefined,
  recurringInterval?: string,
) {
  return priceMetadata?.billingInterval ?? recurringInterval;
}

function formatBrandedPlanName(tierLabel: string) {
  return `${SITE_NAME} · ${tierLabel}`;
}

function readMetadataDisplayName(metadata: Record<string, string> | undefined) {
  const displayName = metadata?.displayNamePt?.trim();
  return displayName ? displayName : undefined;
}

const PLAN_INTERVAL_SUFFIX_PT = {
  month: "mensal",
  year: "anual",
} as const;

const PLAN_COPY_PT: Record<
  PlanCode,
  { title: string; audience: string; limit: string; detail: string; shortLabel: string }
> = {
  free: {
    title: "Grátis",
    audience: "Testes e avaliação",
    limit: "Até 2 projetos",
    detail: "Marca d'água nos relatórios.",
    shortLabel: "Gratuito",
  },
  per_project: {
    title: "Por projeto",
    audience: "Até cerca de 6 projetos",
    limit: "Cobra cada projeto ativo",
    detail: "O valor entra por projeto, não como mensalidade fixa.",
    shortLabel: "Por projeto",
  },
  unlimited: {
    title: "Ilimitado",
    audience: "7 projetos ou mais",
    limit: "Projetos ilimitados",
    detail: "Uma assinatura para a carteira toda.",
    shortLabel: "Ilimitado",
  },
};

function isPlanCode(value: string | undefined): value is PlanCode {
  return PLAN_CODES.some((code) => code === value);
}

export function getPlanCopy(planCode: string | undefined) {
  if (!isPlanCode(planCode)) return null;
  return PLAN_COPY_PT[planCode];
}

export function getProductDisplayName({
  fallbackName,
  productMetadata,
}: {
  fallbackName: string;
  productMetadata: Record<string, string> | undefined;
}) {
  const metadataName = readMetadataDisplayName(productMetadata);
  if (metadataName) return metadataName;

  const copy = getPlanCopy(getProductPlanCode(productMetadata));
  if (copy) return formatBrandedPlanName(copy.title);

  return fallbackName;
}

export function getPlanDisplayName({
  fallbackName,
  priceMetadata,
  productMetadata,
  recurringInterval,
}: {
  fallbackName: string;
  priceMetadata: Record<string, string> | undefined;
  productMetadata: Record<string, string> | undefined;
  recurringInterval?: string;
}) {
  const metadataName =
    readMetadataDisplayName(priceMetadata) ?? readMetadataDisplayName(productMetadata);
  if (metadataName) return metadataName;

  const planCode = resolvePlanCode(priceMetadata, productMetadata);
  const copy = getPlanCopy(planCode);
  if (!copy) return getProductDisplayName({ fallbackName, productMetadata });

  if (planCode === "unlimited") {
    const interval = resolveBillingInterval(priceMetadata, recurringInterval);
    const intervalSuffix =
      interval === "year" ? PLAN_INTERVAL_SUFFIX_PT.year : PLAN_INTERVAL_SUFFIX_PT.month;
    return formatBrandedPlanName(`${copy.title} ${intervalSuffix}`);
  }

  return formatBrandedPlanName(copy.title);
}

export function getPlanShortLabel({
  priceMetadata,
  productMetadata,
  unitAmount,
}: {
  priceMetadata?: Record<string, string>;
  productMetadata?: Record<string, string>;
  unitAmount?: number;
}): string | null {
  const planCode = resolvePlanCode(priceMetadata, productMetadata);
  const billingModel = priceMetadata?.billingModel ?? productMetadata?.billingModel;

  if (planCode === "free" || unitAmount === 0) return PLAN_COPY_PT.free.shortLabel;
  if (planCode === "per_project" || billingModel === "per_project") {
    return PLAN_COPY_PT.per_project.shortLabel;
  }
  if (planCode === "unlimited" || billingModel === "unlimited") {
    return PLAN_COPY_PT.unlimited.shortLabel;
  }
  return getPlanCopy(planCode)?.shortLabel ?? null;
}

export function isPerProjectPlan(
  priceMetadata: Record<string, string> | undefined,
  productMetadata: Record<string, string> | undefined,
) {
  const planCode = resolvePlanCode(priceMetadata, productMetadata);
  const billingModel = priceMetadata?.billingModel ?? productMetadata?.billingModel;
  return planCode === "per_project" || billingModel === "per_project";
}

export function formatRecurringIntervalSuffix({
  interval,
  count,
  isPerProject,
}: {
  interval: string | undefined;
  count: number | undefined;
  isPerProject: boolean;
}) {
  if (!interval) return "";
  const quantity = count && count > 1 ? count : 1;
  let period = "";
  if (interval === "month") period = quantity === 1 ? "/mês" : `/${quantity} meses`;
  else if (interval === "year") period = quantity === 1 ? "/ano" : `/${quantity} anos`;
  else if (interval === "week") period = quantity === 1 ? "/semana" : `/${quantity} semanas`;
  else if (interval === "day") period = quantity === 1 ? "/dia" : `/${quantity} dias`;
  if (!period) return "";
  return isPerProject ? `/projeto${period}` : period;
}

export function planCardActionLabel(planCode: string | undefined, isSignedIn: boolean) {
  if (isSignedIn) return "Assinar no workspace";
  if (planCode === "free") return "Começar grátis";
  return "Quero este plano";
}

export function formatAnnualSavings(monthlyAmount: number, yearlyAmount: number) {
  const yearlyFromMonthly = monthlyAmount * 12;
  if (yearlyFromMonthly <= yearlyAmount) return null;
  const percent = Math.round(((yearlyFromMonthly - yearlyAmount) / yearlyFromMonthly) * 100);
  return `Economize ${percent}% no anual`;
}

export function yearlyAmountAsMonthly(yearlyAmount: number) {
  return Math.round(yearlyAmount / 12);
}

export function sortPricesByInterval<T extends { recurringInterval?: string }>(prices: T[]) {
  const order: Record<string, number> = { month: 0, year: 1, week: 2, day: 3 };
  return [...prices].sort((left, right) => {
    const leftOrder = order[left.recurringInterval ?? ""] ?? 99;
    const rightOrder = order[right.recurringInterval ?? ""] ?? 99;
    return leftOrder - rightOrder;
  });
}
